/**
 * Renders the shared Builder-driven footer from a pre-fetched entry.
 *
 * The Builder `footer` model (entry name "Main Footer") is fetched by the
 * root layout loader and passed here. This component is intentionally thin:
 * it just forwards to `<VisibleChromeContent />`, which renders with the audited
 * chrome-only block registry (COM-182) and warn-logs unknown block names.
 * Scheduling / publishing state is handled by Builder itself — we always get
 * the currently-active entry.
 *
 * Returns `null` when `entry` is falsy — the layout loader legitimately
 * passes `null` when Builder has no "Main Footer" entry for the space, and
 * a FAILED footer fetch is coerced to `null` for the current render
 * (SFCC-12061). The prop is a RESOLVED entry, never a Promise — the layout
 * loader awaits the chrome fan-out before returning (a thenable would be
 * truthy, skip this guard, and break `<Content />` silently).
 */
import { type BuilderContent } from "@builder.io/sdk-react";

import { ModelMap } from "~/hec/builder/model-map";
import { VisibleChromeContent } from "~/hec/builder/visible-chrome-content";
import { useBuilder } from "~/hec/hooks/useBuilder";

interface BuilderFooterProps {
  entry: BuilderContent | null | undefined;
}

export function BuilderFooter({ entry }: BuilderFooterProps) {
  const config = useBuilder();
  if (!entry) return null;

  return (
    <VisibleChromeContent
      model={ModelMap.footer}
      content={entry}
      apiKey={config.config.api_key}
    />
  );
}
